import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LiveTimer } from "@/components/LiveTimer";
import { Badge } from "@/components/ui/badge";
import { Users, Activity } from "lucide-react";
import { formatMSTDateTime, minutesToHours } from "@/lib/timeUtils";

export default function CrewChiefDashboard() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get("/dashboard/crew-chief").then((r) => setData(r.data));
    api.get("/pay-periods/current").then((r) => {
      setPeriod(r.data);
      return api.get(`/clock/sessions?period=${r.data.period_number}`);
    }).then((r) => setSessions(r.data || []));
  }, []);

  if (!data) return null;

  const active = data.team.filter((m) => m.active);
  const idle = data.team.filter((m) => !m.active);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Crew Chief Control · MST</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mt-1">Team status</h1>
        </div>
        {period && (
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Pay Period {period.period_number}</div>
            <div className="text-sm font-medium">{period.range_label}</div>
          </div>
        )}
      </div>

      <div className="mt-8 grid grid-cols-3 gap-4">
        <div className="border border-summit-hairline rounded-lg p-5 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
            <Activity className="w-3 h-3" /> Active now
          </div>
          <div className="font-mono text-4xl mt-2 tracking-tighter" data-testid="active-count">{active.length}</div>
        </div>
        <div className="border border-summit-hairline rounded-lg p-5 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
            <Users className="w-3 h-3" /> Team
          </div>
          <div className="font-mono text-4xl mt-2 tracking-tighter">{data.team.length}</div>
        </div>
        <div className="border border-summit-hairline rounded-lg p-5 bg-white">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Idle</div>
          <div className="font-mono text-4xl mt-2 tracking-tighter">{idle.length}</div>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="font-display text-2xl font-medium tracking-tight">Currently clocked in</h2>
        {active.length === 0 ? (
          <div className="mt-3 text-sm text-gray-500">No engineer is clocked in right now.</div>
        ) : (
          <div className="mt-4 grid md:grid-cols-2 gap-4" data-testid="active-list">
            {active.map((m) => (
              <div key={m.user.id} className="border border-summit-hairline rounded-lg p-5 bg-white timer-active">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{m.user.display_name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                      #{m.user.employee_number} · {m.user.location}
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 font-mono">
                    {m.active.aircraft_tail}
                  </Badge>
                </div>
                <div className="mt-4">
                  <LiveTimer startedAt={m.active.started_at} testId={`team-timer-${m.user.id}`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-12">
        <h2 className="font-display text-2xl font-medium tracking-tight">Team roster</h2>
        <div className="mt-4 border border-summit-hairline rounded-lg bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-summit-hairline">
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Name</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Employee #</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Department</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Location</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.team.map((m) => (
                <tr key={m.user.id} className="border-b border-summit-hairline last:border-0">
                  <td className="px-4 py-2.5">{m.user.display_name}</td>
                  <td className="px-4 py-2.5 font-mono">{m.user.employee_number}</td>
                  <td className="px-4 py-2.5">{m.user.department}</td>
                  <td className="px-4 py-2.5 font-mono">{m.user.location || "—"}</td>
                  <td className="px-4 py-2.5">
                    {m.active ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                        On {m.active.aircraft_tail}
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Idle</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-12">
        <h2 className="font-display text-2xl font-medium tracking-tight">Period sessions</h2>
        <div className="mt-4 border border-summit-hairline rounded-lg bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-summit-hairline">
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">User</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Aircraft</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Started</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Ended</th>
                <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 && (
                <tr><td colSpan="5" className="px-4 py-6 text-center text-gray-400 text-sm">No sessions this period.</td></tr>
              )}
              {sessions.map((s) => (
                <tr key={s.id} className="border-b border-summit-hairline last:border-0">
                  <td className="px-4 py-2.5">{s.user_display}</td>
                  <td className="px-4 py-2.5 font-mono">{s.aircraft_tail}</td>
                  <td className="px-4 py-2.5">{formatMSTDateTime(s.started_at)}</td>
                  <td className="px-4 py-2.5">{s.ended_at ? formatMSTDateTime(s.ended_at) : <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>}</td>
                  <td className="px-4 py-2.5 font-mono text-right">{s.ended_at ? minutesToHours(s.duration_minutes) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
